import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "../../i18n";
import { Brand } from "../ui/brand";
import { Button, ButtonMotionLabel } from "../ui/button";
import { MotionHeading } from "../ui/motion-heading";
import { HomeImageReveal } from "./home-image-reveal";
import { useShowcasePlayback } from "./use-showcase-playback";
import { HomeVideoAccordion, HomeDriftWall } from "./home-galleries";
import { HomeModelShowcase } from "./home-model-showcase";
import type { ResolvedShowcaseMedia } from "../../lib/showcase-media";
import { useShowcaseMedia } from "../showcase-media-provider";

function ShowcaseVideo({ media, hero = false }: { media: ResolvedShowcaseMedia["homeHero"] | ResolvedShowcaseMedia["homeVideos"][number]; hero?: boolean }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  return <>
    <video data-showcase-video src={media.src} poster={media.poster} autoPlay muted loop playsInline
      controls={false} disablePictureInPicture disableRemotePlayback preload={hero ? "auto" : "metadata"} tabIndex={0}
      aria-label={t(media.altKey)} onError={() => setFailed(true)} />
    {failed ? <p role="status" className={`home-media-error
  relative [background:var(--background)] p-3 text-muted-foreground [font-size:12px]
`}>{t("creativeHome.mediaError")}</p> : null}
  </>;
}

function HomeFooter() {
  const { t } = useTranslation();
  return <footer className={`home-footer
  [--background:#0b0b0b] [--foreground:#f5f5f5] [--muted:#202020] [--muted-foreground:#939aa3] [--primary:#f5f5f5] [--border:#303030] [--ring:#c6d6ce] [background:var(--background)] text-foreground [color-scheme:dark] [padding:64px_var(--home-gutter)_32px]
  [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-3 [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0
  [&_li]:[font-size:clamp(16px,1.3vw,20px)] [&_li]:[line-height:1.5]
  [&_li_a]:text-foreground [&_li_a]:no-underline [&_li_a]:outline-offset-1.5
  [&_li_a:hover]:underline [&_li_a:hover]:[text-underline-offset:4px]
  [&_li_a:focus-visible]:[outline:2px_solid_var(--ring)] [&_li_a:focus-visible]:[border-radius:2px]
`}>
    <div className={`home-footer-grid
  grid [grid-template-columns:repeat(4,minmax(0,1fr))] gap-8
  maxw-[760px]:[grid-template-columns:repeat(2,minmax(0,1fr))] maxw-[760px]:[gap:40px_24px]
`}>
      <div><h3>{t("creativeHome.footer.creation")}</h3><ul>
        <li><Link to="/sandbox">{t("creativeHome.playground")}</Link></li>
        <li><Link to="/explore">{t("creativeHome.modelSquare")}</Link></li>
      </ul></div>
      <div><h3>{t("creativeHome.footer.models")}</h3><ul>
        <li><Link to="/explore" search={{ modality: "video" }}>{t("creativeHome.models.video.title")}</Link></li>
        <li><Link to="/explore" search={{ modality: "image" }}>{t("creativeHome.models.image.title")}</Link></li>
        <li><Link to="/explore" search={{ modality: "language" }}>{t("creativeHome.models.language.title")}</Link></li>
        <li><Link to="/explore" search={{ modality: "audio" }}>{t("creativeHome.footer.audioModels")}</Link></li>
        <li><Link to="/explore" search={{ modality: "world" }}>{t("modality.world")}</Link></li>
      </ul></div>
      <div><h3>{t("creativeHome.footer.developer")}</h3><ul>
        <li><Link to="/settings/$" params={{ _splat: "api-keys" }}>{t("workspace.apiKeys")}</Link></li>
        <li><Link to="/requests">{t("nav.requests")}</Link></li>
        <li><Link to="/settings/$" params={{ _splat: "usage" }}>{t("creativeHome.footer.usage")}</Link></li>
      </ul></div>
      <div><h3>{t("creativeHome.footer.about")}</h3><ul>
        <li><Link to="/about-nexra">{t("creativeHome.info.about.title")}</Link></li>
        <li><Link to="/contact">{t("creativeHome.info.contact.title")}</Link></li>
      </ul></div>
    </div>
    <div className={`home-footer-bottom
  flex items-center justify-between gap-8 [margin-top:clamp(96px,12vw,192px)]
  maxw-[760px]:items-start maxw-[760px]:flex-col
`}>
      <div className={`home-footer-brand
  [&_img]:[filter:invert(1)]
`}><Brand size="lg" /></div>

    </div>
  </footer>;
}

export function CreativeHomePage() {
  const showcaseMedia = useShowcaseMedia();
  const showcaseVideos = showcaseMedia.homeVideos;
  const { t } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  useShowcasePlayback(root, showcaseMedia);
  return <div ref={root} className={`creative-home
  [font-family:Inter,"PingFang_SC","Microsoft_YaHei",sans-serif] [line-height:1.6]
  [&_:is(h1,h2,h3,p,figure)]:m-0
  [&_h1]:[font-size:clamp(58px,6.4vw,94px)] [&_h1]:font-normal [&_h1]:[line-height:1.13] [&_h1]:[letter-spacing:-3px]
  [&_h2]:[font-size:clamp(30px,3.4vw,48px)] [&_h2]:font-normal [&_h2]:[line-height:1.3] [&_h2]:[letter-spacing:-1px] [&_h2]:[overflow-wrap:anywhere]
  [&_figure]:min-w-0
  [&_video]:block [&_video]:w-full [&_video]:[aspect-ratio:16/9] [&_video]:object-contain [&_video]:[background:var(--foreground)]
  [&_video::-webkit-media-controls]:hidden!
  [&_video:focus-visible]:[outline:2px_solid_var(--ring)] [&_video:focus-visible]:outline-offset-1
  [&_.home-kicker]:[font-size:12px] [&_.home-kicker]:[letter-spacing:1.9px] [&_.home-kicker]:[line-height:1.5] [&_.home-kicker]:text-muted-foreground [&_.home-kicker]:mb-5
  [&_.home-lead]:[font-size:15px] [&_.home-lead]:[line-height:1.9] [&_.home-lead]:text-muted-foreground [&_.home-lead]:whitespace-pre-line [&_.home-lead]:mt-7
  [&_.home-feature-copy_h2]:whitespace-nowrap [&_.home-feature-copy_h2]:[font-size:min(48px,8cqi)]
  [&_.home-feature-copy_.home-section-note]:max-w-none [&_.home-feature-copy_.home-section-note]:whitespace-nowrap [&_.home-feature-copy_.home-section-note]:[font-size:min(14px,3.4cqi)]
  [&:lang(en)_.home-feature-copy_h2]:[font-size:min(48px,4.2cqi)]
  [&:lang(en)_.home-feature-copy_.home-section-note]:[font-size:min(14px,2cqi)]
  [&_figcaption]:[padding:16px_4px_0]
  [&_figcaption_h3]:[font-size:16px] [&_figcaption_h3]:font-normal [&_figcaption_h3]:[line-height:1.5]
  [&_.home-footer_h3]:[font-size:14px] [&_.home-footer_h3]:font-normal [&_.home-footer_h3]:[line-height:1.5] [&_.home-footer_h3]:text-muted-foreground [&_.home-footer_h3]:mb-6
  maxw-[760px]:[&_h1]:[font-size:clamp(44px,8.1vw,60px)]
  maxw-[760px]:[&_figcaption]:pt-3
  maxw-[760px]:[&_figcaption_h3]:[font-size:16px]
  maxw-[480px]:[&_h1]:[font-size:44px] maxw-[480px]:[&_h1]:[letter-spacing:-2px]
  maxw-[480px]:[&_.home-lead]:[font-size:13px]
  [&_.home-gallery-eyebrow]:mb-3 [&_.home-gallery-eyebrow]:text-muted-foreground [&_.home-gallery-eyebrow]:[font-size:12px] [&_.home-gallery-eyebrow]:[letter-spacing:.12em]
  [&_.home-gallery-description]:mt-5 [&_.home-gallery-description]:[max-width:44em] [&_.home-gallery-description]:[font-size:16px] [&_.home-gallery-description]:[line-height:1.8] [&_.home-gallery-description]:text-muted-foreground
  [&_.home-drift-heading_h2]:whitespace-nowrap [&_.home-drift-heading_h2]:[font-size:min(48px,6.5cqi)]
  [&_.home-drift-heading_.home-gallery-description]:max-w-none [&_.home-drift-heading_.home-gallery-description]:whitespace-nowrap [&_.home-drift-heading_.home-gallery-description]:[font-size:min(16px,2.3cqi)]
  [&:lang(en)_.home-drift-heading_h2]:[font-size:min(48px,6cqi)]
  [&:lang(en)_.home-drift-heading_.home-gallery-description]:[font-size:min(16px,2cqi)]
  [&_.home-video-heading_h2]:whitespace-nowrap [&_.home-video-heading_h2]:[font-size:min(48px,8cqi)]
  [&_.home-video-heading_.home-gallery-description]:max-w-none [&_.home-video-heading_.home-gallery-description]:whitespace-nowrap [&_.home-video-heading_.home-gallery-description]:[font-size:min(16px,3.4cqi)]
  [&:lang(en)_.home-video-heading_h2]:[font-size:min(48px,6cqi)]
  [&:lang(en)_.home-video-heading_.home-gallery-description]:[font-size:min(16px,2cqi)]
  [&_.home-accordion-video]:absolute [&_.home-accordion-video]:inset-0 [&_.home-accordion-video]:w-full [&_.home-accordion-video]:h-full [&_.home-accordion-video]:[border-radius:0] [&_.home-accordion-video]:object-cover [&_.home-accordion-video]:[filter:grayscale(.85)_brightness(.65)] [&_.home-accordion-video]:[transition:filter_.5s]
  [&_.home-gallery-hint]:mt-4 [&_.home-gallery-hint]:text-muted-foreground [&_.home-gallery-hint]:[font-size:12px]
  [&_.home-model-showcase-copy_h2]:[font-size:clamp(28px,3vw,42px)] [&_.home-model-showcase-copy_h2]:[line-height:1.4]
  [&_.home-model-showcase-copy_p]:whitespace-nowrap [&_.home-model-showcase-copy_p]:[margin:32px_auto_0] [&_.home-model-showcase-copy_p]:[font-size:min(22px,1.5cqi)] [&_.home-model-showcase-copy_p]:[line-height:1.7] [&_.home-model-showcase-copy_p]:text-muted-foreground
  maxw-[700px]:[&_.home-model-showcase-copy_p]:mt-5
  motion-reduce:[&_.home-accordion-video]:[transition:none]
  [&_.home-reveal-copy_h2]:grid [&_.home-reveal-copy_h2]:[font-size:clamp(36px,7vw,88px)] [&_.home-reveal-copy_h2]:[line-height:1.12] [&_.home-reveal-copy_h2]:whitespace-pre-line [&_.home-reveal-copy_h2]:[letter-spacing:-.04em]
`} id="home-top" aria-label={t("nav.home")}>
    <section className={`home-hero
  relative isolate [--home-hero-height:calc(100dvh_-_var(--site-header-height,142px)_-_var(--home-gutter))] [height:var(--home-hero-height)] flex items-center justify-center overflow-hidden [margin:0_var(--home-gutter)_var(--home-gutter)] [border-radius:20px] [color:#fff] [--muted-foreground:#ffffffd9] [--ring:#fff]
`} data-video-surface aria-label={t("creativeHome.heroLabel")}>
      <div className={`home-hero-footage
  absolute inset-0 [z-index:-1]
  [&_video]:h-full [&_video]:object-cover [&_video]:object-center
  [&::after]:[content:""] [&::after]:absolute [&::after]:inset-0 [&::after]:[background:linear-gradient(180deg,#09131a33,#09131a8c_50%,#09131a99)] [&::after]:pointer-events-none
`}><ShowcaseVideo key={showcaseMedia.homeHero.src} media={showcaseMedia.homeHero} hero /></div>
      <div className={`home-hero-copy
  w-full [max-width:1000px] [padding:24px_32px] text-center [transform:translateY(min(calc(var(--home-hero-height)_*_.08),max(0px,calc((var(--home-hero-height)_-_100%)_/_2))))] pointer-events-none
  [&_a]:pointer-events-auto
  maxw-[760px]:p-6
  maxw-[480px]:p-4
`}>
        <p className="home-kicker">NEXRA / CREATIVE SPACE</p>
        <MotionHeading as="h1" text={t("creativeHome.heroTitle")} />
        <p className="home-lead">{t("creativeHome.heroDescription")}</p>
        <div className={`home-actions
  flex flex-wrap justify-center gap-6 mt-7
`}>
          <Button asChild variant="showcase-text" motion><Link to="/sandbox" aria-label={t("creativeHome.start")}><ButtonMotionLabel text={t("creativeHome.start")} /></Link></Button>
          <Button asChild variant="showcase-text" motion><a href="#home-videos" aria-label={t("creativeHome.discover")}><ButtonMotionLabel text={t("creativeHome.discover")} /></a></Button>
        </div>
      </div>
    </section>
    <div className={`home-content
  [max-width:1800px] m-auto [padding:0_var(--home-gutter)]
  [&_video]:[aspect-ratio:auto] [&_video]:[border-radius:20px]
`}>
      <HomeModelShowcase />
      <HomeVideoAccordion />
      <section id="home-videos" className={`home-section
  pt-25 [scroll-margin-top:calc(var(--site-navigation-height,79px)_+_24px)]
  maxw-[760px]:pt-18
`}>
        <div className={`home-video-feature
  mb-12 [container-type:inline-size]
`}>
          <div className={`home-feature-copy
  flex flex-col items-center gap-6 text-center
`}>
            <MotionHeading text={t("creativeHome.videoTitle")} />
            <p className={`home-section-note
  [font-size:14px] [line-height:1.85] whitespace-pre-line text-muted-foreground [max-width:30em]
`}>{t("creativeHome.videoDescription")}</p>
          </div>
        </div>
        <div className={`home-video-gallery
  grid [grid-template-columns:repeat(3,minmax(0,1fr))] items-start gap-8
  [&.home-video-gallery_video]:aspect-video [&_video]:object-cover
  [&_figcaption]:text-center
  maxw-[760px]:[grid-template-columns:repeat(2,minmax(0,1fr))] maxw-[760px]:gap-5
  maxw-[600px]:[grid-template-columns:1fr] maxw-[600px]:gap-7
`}>{showcaseVideos.map(media =>
          <figure key={media.id}><ShowcaseVideo key={media.src} media={media} /><figcaption><h3>{t(media.titleKey)}</h3></figcaption></figure>
        )}</div>
      </section>
      <HomeDriftWall />
    </div>
    <HomeImageReveal />
    <HomeFooter />
  </div>;
}
